import * as React from "react";
import {
  List,
  Datagrid,
  TextField,
  EmailField,
  BooleanField,
  FunctionField,
  SearchInput,
  Show,
  SimpleShowLayout,
  DeleteButton,
  TopToolbar,
  ListButton,
  useNotify,
} from "react-admin";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { httpClient } from "../../api/httpClient";
import { API_BASE_URL } from "../../api/config";
import { FrDateField } from "../../components/FrDateField";
import { UserRefField, TechInfo } from "../../components/ui";

// Comptes « incomplets ou d'un autre format » : invitations jamais
// finalisées, lignes sans type (invisibles dans « Tous les patients » /
// « Tous les pros »), ancien format OAuth, comptes Firebase orphelins.
// Ce sont eux qui faisaient répondre « email déjà utilisé » à l'inscription
// alors que personne n'apparaissait dans le BO.
//
// Backend : GET/DELETE /admin/accounts/incomplete[/{id}],
//           GET /admin/accounts/lookup?email=,
//           DELETE /admin/accounts/firebase?email=

const BASE = `${API_BASE_URL}/admin/accounts`;

const fmtTs = (v) => (v ? new Date(Number(v)).toLocaleString("fr-FR") : "—");

const DELETE_CONFIRM =
  "Le compte est anonymisé (email libéré) et le compte de connexion Firebase " +
  "associé est supprimé, sauf si un autre compte ioUman actif l'utilise. " +
  "La personne pourra se réinscrire avec cet email. Aucun email ne lui est envoyé.";

// ── Diagnostic d'un email ────────────────────────────────────────────────
const VERDICT_LABEL = {
  ACTIVE_ACCOUNT: { label: "Compte actif", color: "success" },
  INCOMPLETE_BACKEND_ACCOUNT: { label: "Compte incomplet", color: "warning" },
  FIREBASE_ORPHAN: { label: "Firebase orphelin", color: "warning" },
  NO_ACCOUNT: { label: "Aucun compte", color: "default" },
  UNKNOWN_FIREBASE_UNAVAILABLE: { label: "Firebase injoignable", color: "error" },
};

export const EmailLookupCard = () => {
  const notify = useNotify();
  const [email, setEmail] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [report, setReport] = React.useState(null);

  const lookup = async (target) => {
    const value = (target ?? email).trim();
    if (!value) return;
    setLoading(true);
    try {
      const { json } = await httpClient(`${BASE}/lookup?email=${encodeURIComponent(value)}`);
      setReport(json);
    } catch (e) {
      notify(`Erreur : ${e.message}`, { type: "error" });
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  const deleteRow = async (row) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Supprimer le compte incomplet ${row.email} ?\n\n${DELETE_CONFIRM}`)) return;
    setBusy(true);
    try {
      const { json } = await httpClient(`${BASE}/incomplete/${encodeURIComponent(row.id)}`, {
        method: "DELETE",
      });
      notify(`Compte supprimé. ${json?.firebaseNote || ""}`, { type: "success" });
      await lookup(report?.email);
    } catch (e) {
      notify(`Suppression refusée : ${e.message}`, { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  const deleteFirebase = async () => {
    if (!report?.email) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      `Supprimer le compte de connexion Firebase de ${report.email} ?\n\n` +
      "Refusé automatiquement si un compte ioUman actif utilise cet email.",
    )) return;
    setBusy(true);
    try {
      const { json } = await httpClient(`${BASE}/firebase?email=${encodeURIComponent(report.email)}`, {
        method: "DELETE",
      });
      notify(json?.firebaseNote || "Compte Firebase traité", {
        type: json?.firebaseDeleted ? "success" : "warning",
      });
      await lookup(report.email);
    } catch (e) {
      notify(`Suppression refusée : ${e.message}`, { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  const verdict = report ? VERDICT_LABEL[report.verdict] || { label: report.verdict, color: "default" } : null;
  const fb = report?.firebase;
  const hasCompleteAccount = report?.accounts?.some((a) => !a.incomplete);

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Diagnostiquer un email (« email déjà utilisé » mais compte introuvable)
        </Typography>
        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          <MuiTextField
            fullWidth
            size="small"
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && lookup()}
            inputProps={{ "data-testid": "incomplete-lookup-email" }}
          />
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} /> : <SearchIcon />}
            onClick={() => lookup()}
            disabled={loading || !email.trim()}
          >
            Analyser
          </Button>
        </Box>

        {report && (
          <Stack spacing={2}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Chip label={verdict.label} color={verdict.color} />
              <Typography variant="body2">{report.email}</Typography>
            </Box>
            <Alert severity={report.verdict === "ACTIVE_ACCOUNT" || report.verdict === "NO_ACCOUNT" ? "info" : "warning"}>
              {report.advice}
            </Alert>

            <Box>
              <Typography variant="subtitle2" gutterBottom>Comptes ioUman (base de données)</Typography>
              {report.accounts?.length ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nom</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Statut</TableCell>
                      <TableCell>Diagnostic</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {report.accounts.map((a) => (
                      <TableRow key={a.id}>
                        <TableCell>{[a.firstName, a.lastName].filter(Boolean).join(" ") || "—"}</TableCell>
                        <TableCell>{a.email}</TableCell>
                        <TableCell>{a.roleType || "—"} / {a.userType || "—"}</TableCell>
                        <TableCell>{a.accountStatus || "—"}</TableCell>
                        <TableCell>{a.diagnosis}</TableCell>
                        <TableCell align="right">
                          {a.incomplete ? (
                            <Button size="small" color="error" disabled={busy} onClick={() => deleteRow(a)}>
                              Supprimer
                            </Button>
                          ) : (
                            <Chip size="small" label="Compte actif" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="text.secondary">Aucune ligne en base pour cet email.</Typography>
              )}
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>Compte de connexion (Firebase Authentication)</Typography>
              {!fb?.available ? (
                <Typography variant="body2" color="error">
                  Firebase injoignable depuis le serveur{fb?.error ? ` (${fb.error})` : ""} — vérifier dans la console Firebase.
                </Typography>
              ) : fb.exists ? (
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                  <Typography variant="body2">
                    uid <code>{fb.uid}</code> · fournisseurs : {fb.providers?.join(", ") || "aucun (pas de mot de passe)"}
                    {" "}· créé le {fmtTs(fb.createdAt)} · dernière connexion : {fmtTs(fb.lastSignInAt)}
                    {fb.disabled ? " · DÉSACTIVÉ" : ""}
                  </Typography>
                  {!hasCompleteAccount && (
                    <Button size="small" color="error" variant="outlined" disabled={busy} onClick={deleteFirebase}>
                      Supprimer le compte Firebase
                    </Button>
                  )}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">Aucun compte Firebase pour cet email.</Typography>
              )}
            </Box>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

// ── Liste des comptes incomplets ─────────────────────────────────────────
const filters = [<SearchInput key="q" source="q" alwaysOn placeholder="Nom ou email" />];

export const IncompleteAccountList = () => (
  <Box>
    <EmailLookupCard />
    <List
      resource="incomplete-accounts"
      filters={filters}
      perPage={25}
      sort={{ field: "createdAt", order: "DESC" }}
      title="Comptes incomplets / autres formats"
      exporter={false}
    >
      <Datagrid rowClick="show" bulkActionButtons={false}>
        <TextField source="firstName" label="Prénom" />
        <TextField source="lastName" label="Nom" />
        <EmailField source="email" />
        <FunctionField label="Type" render={(r) => `${r.roleType || "—"} / ${r.userType || "—"}`} />
        <TextField source="accountStatus" label="Statut" emptyText="—" />
        <FunctionField label="Firebase" render={(r) => (r.uid ? "Relié" : "—")} />
        <FrDateField source="createdAt" label="Créé le" emptyText="—" />
        <TextField source="diagnosis" label="Diagnostic" sortable={false} />
        <DeleteButton
          mutationMode="pessimistic"
          confirmTitle="Supprimer ce compte incomplet ?"
          confirmContent={DELETE_CONFIRM}
        />
      </Datagrid>
    </List>
  </Box>
);

const ShowActions = () => (
  <TopToolbar>
    <ListButton />
    <DeleteButton
      mutationMode="pessimistic"
      confirmTitle="Supprimer ce compte incomplet ?"
      confirmContent={DELETE_CONFIRM}
    />
  </TopToolbar>
);

export const IncompleteAccountShow = () => (
  <Show actions={<ShowActions />}>
    <SimpleShowLayout>
      <TextField source="firstName" label="Prénom" emptyText="—" />
      <TextField source="lastName" label="Nom" emptyText="—" />
      <EmailField source="email" />
      <TextField source="roleType" label="Type (user_role_type)" emptyText="— (manquant)" />
      <TextField source="userType" label="Discriminant (user_type)" emptyText="—" />
      <TextField source="accountStatus" label="Statut" emptyText="—" />
      <BooleanField source="hasPassword" label="Mot de passe backend" />
      <BooleanField source="enabled" label="Enabled" />
      <BooleanField source="active" label="Actif" />
      <TextField source="provider" label="Provider" emptyText="—" />
      <UserRefField source="invitedByProfessionalId" label="Invité par" />
      <FrDateField source="createdAt" label="Créé le" showTime emptyText="—" />
      <BooleanField source="incomplete" label="Incomplet" />
      <TextField source="diagnosis" label="Diagnostic" />
      <TechInfo />
        </SimpleShowLayout>
  </Show>
);

export default {
  list: IncompleteAccountList,
  show: IncompleteAccountShow,
};
